/**
 * HomePage v2
 * ───────────
 * Enhanced with VVS Motion System.
 * Awwwards-level animations with GSAP.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Instagram, ChevronRight, ExternalLink, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import SponsorReel from '../components/SponsorReel';
import { MagneticRouterLink } from '../components/MagneticButton';
import {
  VVS_MOTION,
  prefersReducedMotion,
  isMobile as checkMobile,
  getIntensity,
  AnimatedTitle,
  AnimatedSection,
  GlitchText,
  useParticleBurst,
} from '../motion';

gsap.registerPlugin(ScrollTrigger);

const VVS_LOGO = 'https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/yqc71nna_FromNow2.jpg';
const TEX2 = 'https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/kq1h86ag_download.jpg';
const TEX3 = 'https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/1x77s1n5_Honor%20MagicOS%209%20Wallpaper%20-6.jpg';
const TEX4 = 'https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/kayy05kw_download%20%283%29.jpg';
const TEX1 = 'https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/dnyeucox_Wallpaper%20by%20%40koshiera.jpg';
const WAVE_PAINTING = 'https://customer-assets.emergentagent.com/job_7ae2fa0d-fc89-4b84-8ee2-77e1b09b9f84/artifacts/opgfzud2_Wave_Explorer_03_Affiche.jpg';

const PORTAL_TILES = [
  { key: 'portals.vision', descKey: 'portals.vision_desc', path: '/vision', bg: TEX3 },
  { key: 'portals.events', descKey: 'portals.events_desc', path: '/events', bg: TEX1 },
  { key: 'portals.artists', descKey: 'portals.artists_desc', path: '/artists', bg: TEX2 },
];

// Contact Drawer
function ContactDrawer({ onClose }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const drawerRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    gsap.from(drawerRef.current, {
      y: '100%',
      opacity: 0,
      duration: 0.5,
      ease: VVS_MOTION.enter,
    });
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, backdropFilter: 'blur(4px)' }} />
      <div ref={drawerRef} data-testid="contact-drawer" style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'var(--vvs-surface)', borderTop: '1px solid rgba(240,244,248,0.1)',
        zIndex: 201, padding: '32px 40px 40px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '18px', textTransform: 'uppercase', color: '#F0F4F8', margin: 0 }}>
            {t('contact_section.title')}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--vvs-muted)', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', color: 'var(--vvs-muted)', marginBottom: '24px' }}>
          {t('contact_section.desc')}
        </p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button onClick={() => { onClose(); navigate('/contact'); }} className="btn-metallic btn-metallic-filled" data-testid="go-to-contact" style={{ cursor: 'pointer' }}>
            {t('contact.send')} →
          </button>
          <a href="mailto:contact@vaguevisionsound.com" className="btn-metallic" style={{ textDecoration: 'none' }}>
            contact@vaguevisionsound.com
          </a>
        </div>
      </div>
    </>
  );
}

export default function HomePageV2() {
  const { t } = useLanguage();
  const heroRef = useRef(null);
  const logoRef = useRef(null);
  const ctaRef = useRef(null);
  const [showContact, setShowContact] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const burst = useParticleBurst({ count: 25, colors: ['#00C8DC', '#00A0B0', '#0088CC'] });
  
  // Check for mobile
  useEffect(() => {
    const check = () => setIsMobile(checkMobile());
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Hero cinematic entrance
  useEffect(() => {
    document.title = 'Vague Vision Sound // Where sound becomes space.';
    if (prefersReducedMotion() || !heroRef.current) return;

    const intensity = getIntensity();
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: VVS_MOTION.wave },
      });

      // t=0: Logo fade in with scale
      tl.from(logoRef.current, {
        scale: 0.7,
        opacity: 0,
        duration: VVS_MOTION.cinematic,
        ease: VVS_MOTION.wave,
      }, 0.3);

      // Eyebrow
      tl.from('.hero-eyebrow', {
        y: 30 * intensity,
        opacity: 0,
        duration: VVS_MOTION.normal,
      }, 0.5);

      // Main title with stagger
      tl.from('.hero-title-word', {
        y: 60 * intensity,
        opacity: 0,
        rotateX: 20 * intensity,
        duration: VVS_MOTION.slow,
        stagger: VVS_MOTION.staggerNormal,
      }, 0.7);

      // Lines
      tl.from('.hero-line', {
        y: 30 * intensity,
        opacity: 0,
        duration: VVS_MOTION.normal,
        stagger: VVS_MOTION.staggerFast,
      }, '-=0.5');

      // Micro tagline
      tl.from('.hero-micro', {
        y: 15 * intensity,
        opacity: 0,
        duration: VVS_MOTION.fast,
      }, '-=0.3');

      // CTAs with spring
      tl.from('.hero-cta', {
        scale: 0.9,
        opacity: 0,
        duration: VVS_MOTION.normal,
        stagger: VVS_MOTION.staggerFast,
        ease: VVS_MOTION.spring,
      }, '-=0.2');

      // Scroll indicator pulse
      tl.from('.scroll-indicator', {
        opacity: 0,
        y: -20,
        duration: VVS_MOTION.normal,
      }, '-=0.3');

      // Background parallax
      gsap.to('.hero-bg', {
        y: 100 * intensity,
        ease: 'none',
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });

    }, heroRef);

    return () => ctx.revert();
  }, []);

  // CTA click with particle burst
  const handleCtaClick = useCallback((e) => {
    burst(e.clientX, e.clientY);
  }, [burst]);

  // State transition callback for particle field
  const handleSectionEnter = useCallback((sectionName) => {
    if (window.__vvsUpdateParticleIntensity) {
      switch (sectionName) {
        case 'portals':
          window.__vvsUpdateParticleIntensity(0.7);
          break;
        case 'release':
          window.__vvsUpdateParticleIntensity(0.5);
          break;
        case 'events':
          window.__vvsUpdateParticleIntensity(0.8);
          break;
        default:
          window.__vvsUpdateParticleIntensity(1);
      }
    }
  }, []);

  return (
    <div style={{ position: 'relative', zIndex: 1, paddingTop: '64px', overflowX: 'hidden' }}>

      {/* HERO - ACT 1 */}
      <section ref={heroRef} style={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        textAlign: 'center',
        padding: isMobile ? '60px 20px' : '80px 48px',
        position: 'relative', overflow: 'hidden',
      }} data-testid="hero-section">
        
        {/* Background with parallax */}
        <div 
          className="hero-bg"
          style={{
            position: 'absolute',
            inset: '-20%',
            background: `linear-gradient(to bottom, rgba(2, 8, 20, 0.62) 0%, rgba(2, 8, 20, 0.78) 50%, #020814 100%), url(${WAVE_PAINTING}) center 30% / cover no-repeat`,
            willChange: 'transform',
          }}
        />

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <p className="hero-eyebrow caption" style={{ marginBottom: isMobile ? '28px' : '40px', color: 'rgba(148,163,184,0.5)' }}>
            {t('hero.tagline')}
          </p>

          <img
            ref={logoRef}
            src={VVS_LOGO}
            alt="Vague Vision Sound"
            data-testid="hero-logo"
            style={{
              width: isMobile ? '140px' : 'clamp(160px, 22vw, 260px)',
              height: isMobile ? '140px' : 'clamp(160px, 22vw, 260px)',
              objectFit: 'cover', borderRadius: '50%',
              marginBottom: isMobile ? '36px' : '52px',
              boxShadow: '0 0 60px rgba(0,50,100,0.35), 0 0 120px rgba(0,30,70,0.2)',
              willChange: 'transform, opacity',
              display: 'block',
              margin: '0 auto',
              marginBottom: isMobile ? '36px' : '52px',
            }}
          />

          {/* Title with split words */}
          <h1 className="neon-heading" style={{
            fontSize: isMobile ? '28px' : 'clamp(32px, 5.5vw, 60px)',
            lineHeight: 1, letterSpacing: '0.04em', textTransform: 'uppercase',
            marginBottom: isMobile ? '20px' : '28px',
            display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0 0.3em',
          }}>
            {['VAGUE', 'VISION', 'SOUND'].map((word, i) => (
              <span key={i} className="hero-title-word" style={{ display: 'inline-block', willChange: 'transform, opacity' }}>
                {i === 1 ? <GlitchText duration={0.5}>{word}</GlitchText> : word}
              </span>
            ))}
          </h1>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: isMobile ? '24px' : '32px', maxWidth: '100%', padding: '0 10px' }}>
            {['hero.line1', 'hero.line2', 'hero.line3'].map((key, i) => (
              <p key={i} className="hero-line" style={{
                fontFamily: i === 0 ? 'Syne, sans-serif' : 'Space Grotesk, sans-serif',
                fontWeight: i === 0 ? 700 : 400,
                fontSize: i === 0 ? (isMobile ? '16px' : 'clamp(17px, 2.5vw, 26px)') : (isMobile ? '14px' : 'clamp(14px, 2vw, 20px)'),
                color: i === 0 ? '#F0F4F8' : 'var(--vvs-muted)',
                letterSpacing: i === 0 ? '-0.01em' : '0.04em',
                willChange: 'transform, opacity',
              }}>
                {t(key)}
              </p>
            ))}
          </div>

          <p className="hero-micro caption" style={{ marginBottom: isMobile ? '28px' : '40px', color: 'rgba(0,240,255,0.35)' }}>
            {t('hero.tagline')}
          </p>

          <div ref={ctaRef} style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <MagneticRouterLink 
              to="/artists" 
              className="hero-cta btn-metallic btn-metallic-filled" 
              data-testid="hero-cta-enter" 
              style={{ textDecoration: 'none', willChange: 'transform, opacity' }}
              onClick={handleCtaClick}
            >
              {t('hero.enter')}
            </MagneticRouterLink>
            <MagneticRouterLink 
              to="/artists/fromnow" 
              className="hero-cta btn-metallic" 
              data-testid="hero-cta-listen" 
              style={{ textDecoration: 'none', willChange: 'transform, opacity' }}
              onClick={handleCtaClick}
            >
              {t('hero.listen')}
            </MagneticRouterLink>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="scroll-indicator" style={{ 
          position: 'absolute', bottom: '32px', left: '50%', transform: 'translateX(-50%)', 
          width: '1px', height: '48px', 
          background: 'linear-gradient(to bottom, rgba(148,163,184,0.2), transparent)',
          willChange: 'opacity, transform',
        }} />
      </section>

      {/* A UNIVERSE YOU CAN FEEL - ACT 2 */}
      <AnimatedSection 
        act={2} 
        onEnter={() => handleSectionEnter('portals')}
        style={{ padding: isMobile ? '60px 20px' : '80px 48px', borderTop: '1px solid rgba(240,244,248,0.04)' }}
      >
        <div data-testid="portals-section">
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <p className="caption" style={{ marginBottom: '36px' }}>{t('portals.title')}</p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: isMobile ? '16px' : '2px',
            }}>
              {PORTAL_TILES.map((tile, index) => (
                <Link key={tile.path} to={tile.path} data-testid={`portal-${tile.path.slice(1)}`}
                  style={{ 
                    textDecoration: 'none', position: 'relative', overflow: 'hidden', 
                    height: isMobile ? '200px' : '300px', display: 'block', 
                    borderRadius: isMobile ? '8px' : '0',
                    opacity: 0,
                    transform: 'translateY(40px)',
                    animation: `fadeInUp 0.8s ${VVS_MOTION.wave} ${0.1 * index}s forwards`,
                  }}
                  className="liquid-card"
                >
                  <img src={tile.bg} alt={t(tile.key)} style={{ 
                    position: 'absolute', inset: 0, width: '100%', height: '100%', 
                    objectFit: 'cover', opacity: 0.3, transition: 'opacity 0.5s ease, transform 0.6s ease' 
                  }}
                    onMouseEnter={e => { if (!isMobile) { e.currentTarget.style.opacity = '0.45'; e.currentTarget.style.transform = 'scale(1.04)'; }}}
                    onMouseLeave={e => { if (!isMobile) { e.currentTarget.style.opacity = '0.3'; e.currentTarget.style.transform = 'scale(1)'; }}}
                  />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(5,10,20,0.88) 0%, transparent 65%)' }} />
                  <div style={{ position: 'absolute', bottom: isMobile ? '20px' : '28px', left: isMobile ? '20px' : '28px', right: isMobile ? '20px' : '28px' }}>
                    <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '22px' : '26px', textTransform: 'uppercase', color: '#F0F4F8', marginBottom: '5px' }}>
                      {t(tile.key)}
                    </h3>
                    <p style={{ fontFamily: 'Space Grotesk', fontSize: '12px', color: 'var(--vvs-muted)', letterSpacing: '0.04em' }}>
                      {t(tile.descKey)}
                    </p>
                  </div>
                  <ChevronRight size={16} style={{ position: 'absolute', top: '20px', right: '20px', color: 'rgba(240,244,248,0.25)' }} />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* LAST RELEASE - ACT 3 (slower, contemplative) */}
      <AnimatedSection 
        act={3} 
        onEnter={() => handleSectionEnter('release')}
        style={{ padding: isMobile ? '60px 20px' : '80px 48px', borderTop: '1px solid rgba(240,244,248,0.04)' }}
      >
        <div data-testid="last-release-section">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <p className="caption" style={{ marginBottom: '24px', color: 'var(--vvs-cyan)' }}>Last Release</p>
            <AnimatedTitle
              as="h2"
              animation="curtain"
              className="neon-heading"
              style={{
                fontSize: isMobile ? '42px' : 'clamp(48px, 8vw, 90px)',
                lineHeight: 0.9, letterSpacing: '-0.02em', textTransform: 'uppercase',
                marginBottom: '28px',
              }}
            >
              FromZero III
            </AnimatedTitle>
            <p style={{
              fontFamily: 'Space Grotesk', fontSize: isMobile ? '15px' : 'clamp(15px, 2vw, 19px)',
              color: 'var(--vvs-steel)', marginBottom: '40px', letterSpacing: '0.02em',
              maxWidth: '500px', margin: '0 auto 40px',
              padding: '0 10px',
            }}>
              A new chapter. Built from pressure and reconstruction.
            </p>
            <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href="https://soundcloud.com/fromnowproductions/sets/fromzero-iii"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-metallic btn-metallic-filled"
                data-testid="last-release-listen"
                style={{ textDecoration: 'none', display: 'inline-block' }}
                onClick={handleCtaClick}
              >
                Listen
              </a>
              <Link to="/releases" className="btn-metallic" data-testid="last-release-view-all" style={{ textDecoration: 'none', display: 'inline-block' }}>
                View Releases
              </Link>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* EVENTS TEASER - ACT 4 (return to energy) */}
      <AnimatedSection 
        act={4}
        onEnter={() => handleSectionEnter('events')}
        style={{ padding: isMobile ? '60px 20px' : '80px 48px', borderTop: '1px solid rgba(240,244,248,0.04)' }}
      >
        <div data-testid="events-teaser-section">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <p className="caption" style={{ marginBottom: '20px' }}>Next memory.</p>
            <AnimatedTitle
              as="h2"
              animation="wave"
              className="neon-heading"
              style={{
                fontSize: isMobile ? '48px' : 'clamp(48px, 10vw, 100px)',
                lineHeight: 0.85, letterSpacing: '-0.02em', textTransform: 'uppercase',
                marginBottom: '20px',
              }}
            >
              WITHIN
            </AnimatedTitle>
            <p style={{
              fontFamily: 'Space Grotesk', fontSize: isMobile ? '14px' : 'clamp(14px, 2vw, 18px)',
              color: 'var(--vvs-muted)', marginBottom: '36px', letterSpacing: '0.04em',
            }}>
              26.02.2026 // Brussels Planetarium
            </p>
            <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/events/within" className="btn-metallic btn-metallic-filled" data-testid="within-discover-btn"
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                onClick={handleCtaClick}
              >
                Discover WITHIN <ChevronRight size={14} />
              </Link>
              <a href="https://vague-vision-sound.fairtix.io/p/regular-sale" target="_blank" rel="noopener noreferrer"
                className="btn-metallic" data-testid="within-tickets-btn"
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                Get Tickets <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Sponsor Reel */}
      <SponsorReel />

      {/* CONTACT TEASER */}
      <AnimatedSection act={4} style={{ padding: isMobile ? '60px 20px 50px' : '80px 48px 60px', borderTop: '1px solid rgba(240,244,248,0.04)' }}>
        <div style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
          <p className="caption" style={{ marginBottom: '16px' }}>{t('contact_section.title')}</p>
          <p style={{ fontFamily: 'Space Grotesk', fontSize: '14px', color: 'var(--vvs-muted)', lineHeight: 1.7, marginBottom: '28px' }}>
            {t('contact_section.desc')}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-metallic btn-metallic-filled" data-testid="home-contact-btn" style={{ textDecoration: 'none' }} onClick={handleCtaClick}>
              Contact
            </Link>
            <a href="https://www.instagram.com/vaguevisionsound/" target="_blank" rel="noopener noreferrer"
              className="btn-metallic" data-testid="home-instagram" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <Instagram size={13} /> Instagram
            </a>
          </div>
        </div>
      </AnimatedSection>

      {showContact && <ContactDrawer onClose={() => setShowContact(false)} />}

      {/* Keyframe animations */}
      <style>{`
        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
